import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { addTemporaryToast } from "shared/components/Toast";
import { IAPIState, IStatus } from "shared/types";
import { DeleteModal } from "shared/components";
import { copy } from "shared/assets";

import {
  dataMetaSelector,
  deleteBandUser,
  dispatchGetMyBandUsers,
  dispatchGetUserBands,
} from "Bands/state";
import { BAND_ACTIONS, IBandUser } from "Bands/types";
import {
  useClearActionMetaOnUnMount,
  useLastMemberError,
  useLastMemberWithPermissionError,
} from "Bands/utils";
import { currentUserSelector } from "Auth/state";
import { BAND_PATHS } from "Bands/router";

const deleteMemberCopy = copy.members.deleteMember;

interface DeleteMemberModalProps {
  member: IBandUser;
  handleClose: () => void;
  handleDeleteSuccess?: () => void;
  redirectAfterDelete?: boolean;
  customName?: string;
  customButtonCopy?: string;
  customDeleteCopy?: string;
}

export const DeleteMemberModal: React.FC<DeleteMemberModalProps> = ({
  member,
  handleClose,
  handleDeleteSuccess = () => {},
  redirectAfterDelete = true,
  customName,
  customButtonCopy,
  customDeleteCopy,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const currentUser = useSelector(currentUserSelector);

  const { api: apiMeta } = useSelector(dataMetaSelector(BAND_ACTIONS.deleteBandUser));

  useClearActionMetaOnUnMount(BAND_ACTIONS.deleteBandUser);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      handleClose();
      handleDeleteSuccess();

      // if you removed yourself, update your bands
      if (member.id === currentUser?.id) {
        dispatchGetUserBands(dispatch);
        dispatchGetMyBandUsers(dispatch);
        redirectAfterDelete && history.replace(BAND_PATHS.CALENDAR);
      }

      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: deleteMemberCopy.messages.success,
        })
      );
    }
  }, [
    dispatch,
    apiMeta,
    history,
    handleClose,
    handleDeleteSuccess,
    member.id,
    currentUser?.id,
    redirectAfterDelete,
  ]);

  const handleDelete = () => {
    dispatch(deleteBandUser(member.id));
  };

  const { error: lastMemberError } = useLastMemberError(BAND_ACTIONS.deleteBandUser);
  const { error: lastPermissionError } = useLastMemberWithPermissionError(
    BAND_ACTIONS.deleteBandUser
  );

  if (!member) return null;

  return (
    <DeleteModal
      apiMeta={apiMeta}
      handleClose={handleClose}
      handleDelete={handleDelete}
      name={customName || `${member.firstname} ${member.lastname}`}
      customButtonCopy={customButtonCopy || deleteMemberCopy.buttons.primary}
      customDeleteCopy={customDeleteCopy || deleteMemberCopy.messages.modal}
      error={lastMemberError || lastPermissionError}
    ></DeleteModal>
  );
};
